
import { Input } from "@/components/ui/input";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { CourseDataType } from "./CourseForm";

type CourseBasicFieldsProps = {
  formData: CourseDataType;
  setFormData: (data: CourseDataType) => void;
  isEditing: boolean;
};

// US states array for the dropdown
const US_STATES = [
  { value: "none", label: "Select a state" },
  { value: "AL", label: "Alabama" },
  { value: "AK", label: "Alaska" },
  { value: "AZ", label: "Arizona" },
  { value: "AR", label: "Arkansas" },
  { value: "CA", label: "California" },
  { value: "CO", label: "Colorado" },
  { value: "CT", label: "Connecticut" },
  { value: "DE", label: "Delaware" },
  { value: "FL", label: "Florida" },
  { value: "GA", label: "Georgia" },
  { value: "HI", label: "Hawaii" },
  { value: "ID", label: "Idaho" },
  { value: "IL", label: "Illinois" },
  { value: "IN", label: "Indiana" },
  { value: "IA", label: "Iowa" },
  { value: "KS", label: "Kansas" },
  { value: "KY", label: "Kentucky" },
  { value: "LA", label: "Louisiana" },
  { value: "ME", label: "Maine" },
  { value: "MD", label: "Maryland" },
  { value: "MA", label: "Massachusetts" },
  { value: "MI", label: "Michigan" },
  { value: "MN", label: "Minnesota" },
  { value: "MS", label: "Mississippi" },
  { value: "MO", label: "Missouri" },
  { value: "MT", label: "Montana" },
  { value: "NE", label: "Nebraska" },
  { value: "NV", label: "Nevada" },
  { value: "NH", label: "New Hampshire" },
  { value: "NJ", label: "New Jersey" },
  { value: "NM", label: "New Mexico" },
  { value: "NY", label: "New York" },
  { value: "NC", label: "North Carolina" },
  { value: "ND", label: "North Dakota" },
  { value: "OH", label: "Ohio" },
  { value: "OK", label: "Oklahoma" },
  { value: "OR", label: "Oregon" },
  { value: "PA", label: "Pennsylvania" },
  { value: "RI", label: "Rhode Island" },
  { value: "SC", label: "South Carolina" },
  { value: "SD", label: "South Dakota" },
  { value: "TN", label: "Tennessee" },
  { value: "TX", label: "Texas" },
  { value: "UT", label: "Utah" },
  { value: "VT", label: "Vermont" },
  { value: "VA", label: "Virginia" },
  { value: "WA", label: "Washington" },
  { value: "WV", label: "West Virginia" },
  { value: "WI", label: "Wisconsin" },
  { value: "WY", label: "Wyoming" },
  { value: "DC", label: "District of Columbia" },
];

export function CourseBasicFields({ formData, setFormData, isEditing }: CourseBasicFieldsProps) {
  // Helper function to handle state value safely
  const handleStateChange = (value: string) => {
    // If "none" is selected, set state to null in the database
    const stateValue = value === "none" ? null : value;
    setFormData({ ...formData, state: stateValue });
  };

  // Prepare the state value for the Select component
  // If state is null or empty, use "none" to avoid Radix UI empty string issues
  const stateSelectValue = formData.state || "none";

  return (
    <div className="grid gap-4 md:grid-cols-2">
      <div className="space-y-2">
        <label className="text-sm font-medium">Course Name</label>
        {isEditing ? (
          <Input
            value={formData.course_name}
            onChange={(e) =>
              setFormData({ ...formData, course_name: e.target.value })
            }
          />
        ) : (
          <div>{formData.course_name}</div>
        )}
      </div>
      <div className="space-y-2">
        <label className="text-sm font-medium">Course Type</label>
        {isEditing ? (
          <Input
            value={formData.course_type}
            onChange={(e) =>
              setFormData({ ...formData, course_type: e.target.value })
            }
          />
        ) : (
          <div>{formData.course_type}</div>
        )}
      </div>
      <div className="space-y-2">
        <label className="text-sm font-medium">Language</label>
        {isEditing ? (
          <Input
            value={formData.language}
            onChange={(e) =>
              setFormData({ ...formData, language: e.target.value })
            }
          />
        ) : (
          <div>{formData.language}</div>
        )}
      </div>
      <div className="space-y-2">
        <label className="text-sm font-medium">State</label>
        {isEditing ? (
          <Select 
            value={stateSelectValue} 
            onValueChange={handleStateChange}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select state" />
            </SelectTrigger>
            <SelectContent>
              {US_STATES.map((state) => (
                <SelectItem key={state.value} value={state.value}>
                  {state.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        ) : (
          <div>{formData.state || "Not specified"}</div>
        )}
      </div>
      <div className="space-y-2">
        <label className="text-sm font-medium">Duration (hours)</label>
        {isEditing ? (
          <Input
            type="number"
            value={formData.duration_hours}
            onChange={(e) =>
              setFormData({
                ...formData,
                duration_hours: parseFloat(e.target.value),
              })
            }
          />
        ) : (
          <div>{formData.duration_hours}</div>
        )}
      </div>
      <div className="space-y-2">
        <label className="text-sm font-medium">Price</label>
        {isEditing ? (
          <Input
            type="number"
            value={formData.price}
            onChange={(e) =>
              setFormData({ ...formData, price: parseFloat(e.target.value) })
            }
          />
        ) : (
          <div>${formData.price}</div>
        )}
      </div>
      <div className="space-y-2">
        <label className="text-sm font-medium">Original Price</label>
        {isEditing ? (
          <Input
            type="number"
            value={formData.original_price || ""}
            onChange={(e) =>
              setFormData({
                ...formData,
                original_price: e.target.value ? parseFloat(e.target.value) : null,
              })
            }
          />
        ) : (
          <div>{formData.original_price ? `$${formData.original_price}` : "N/A"}</div>
        )}
      </div>
      <div className="space-y-2">
        <label className="text-sm font-medium">State Fee</label>
        {isEditing ? (
          <Input
            type="number"
            value={formData.state_fee || ""}
            onChange={(e) =>
              setFormData({
                ...formData,
                state_fee: e.target.value ? parseFloat(e.target.value) : null,
              })
            }
          />
        ) : (
          <div>{formData.state_fee ? `$${formData.state_fee}` : "N/A"}</div>
        )}
      </div>
    </div>
  );
}
